import { useMemo } from 'react';
import { Link, Outlet, useParams } from 'react-router-dom';
import {
  useGetCampaignById,
  useGetCampaignPages,
  useGetPageById,
} from '@tinct/core-api';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  TabNavLinks,
} from '@tinct/web-components';
import { useCurrentWorkspaceUrlPrefix } from '@tinct/core-workspace';
import { LayoutTopbar, useBreadcrumb } from '@tinct/web-layout';
import { ChevronLeft, ChevronRight, ExternalLink, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  PageActionsMenu,
  PageOutdatedStatusBadge,
  PageStatusBadge,
  getPageDisplayIdentity,
} from '../shared';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB');
}

export function DetailsVariantPage() {
  const { id: campaignId = '', variantId = '' } = useParams<{
    id: string;
    variantId: string;
  }>();
  const workspaceBaseUrl = useCurrentWorkspaceUrlPrefix();

  const { data: campaign } = useGetCampaignById({
    id: campaignId,
    fields: {
      id: null,
      name: null,
      status: null,
      landingPage: {
        fields: {
          url: null,
        },
      },
      snippetStatus: {
        fields: {
          status: null,
        },
      },
    },
  });

  const {
    data: variant,
    isLoading,
    error,
  } = useGetPageById({
    id: variantId,
    fields: {
      id: null,
      status: null,
      outdatedStatus: null,
      createdAt: null,
      updatedAt: null,
      staticUrl: null,
      lastPublishedAt: null,
      targetType: null,
      adCampaign: { fields: { id: null, name: null } },
      lead: {
        fields: {
          id: null,
          company: {
            fields: {
              id: null,
              name: null,
              logo: null,
            },
          },
        },
      },
    },
  });

  // Full variant list for prev/next navigation. V1 assumes ≤200 variants/campaign.
  const { data: pagesList } = useGetCampaignPages({
    staticParams: { campaignId },
    offset: 0,
    limit: 200,
    fields: { id: null },
  });

  const { prevVariantId, nextVariantId } = useMemo(() => {
    const ids = (pagesList?.data ?? [])
      .map((p) => p.id)
      .filter((id): id is string => !!id);
    const idx = ids.indexOf(variantId);
    if (idx === -1) return { prevVariantId: null, nextVariantId: null };
    return {
      prevVariantId: idx > 0 ? ids[idx - 1] : null,
      nextVariantId: idx < ids.length - 1 ? ids[idx + 1] : null,
    };
  }, [pagesList, variantId]);

  const breadcrumbs = useBreadcrumb(
    [
      { label: 'Dashboard', to: `${workspaceBaseUrl}/dashboard` },
      { label: 'Campaigns', to: `${workspaceBaseUrl}/campaigns` },
      {
        label: campaign?.name ?? '—',
        to: `${workspaceBaseUrl}/campaigns/${campaignId}`,
      },
    ],
    true,
  );

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col">
        <header className="sticky top-0 z-30">
          <LayoutTopbar breadcrumbs={breadcrumbs} />
        </header>
        <div className="flex flex-1 items-center justify-center py-24">
          <span className="text-muted-foreground">Loading variant...</span>
        </div>
      </div>
    );
  }

  if (error?.httpCode === 403 || !variant) {
    return (
      <div className="flex flex-1 flex-col">
        <header className="sticky top-0 z-30">
          <LayoutTopbar breadcrumbs={breadcrumbs} />
        </header>
        <div className="flex flex-1 flex-col items-center justify-center gap-4 py-24 text-center">
          <p className="text-sm font-medium">
            This variant has not been published yet
          </p>
          <p className="max-w-md text-sm text-muted-foreground">
            Analytics become available once a variant reaches the Published or
            Unpublished state.
          </p>
          <Button asChild variant="outline" size="sm">
            <Link to={`${workspaceBaseUrl}/campaigns/${campaignId}/landing-pages`}>
              Back to campaign
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const company = variant.lead?.company ?? null;
  const companyName = getPageDisplayIdentity(variant).label;
  const companyLogoUrl = company?.logo?.url ?? undefined;
  const companyInitials = companyName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const landingPageUrl = campaign?.landingPage?.url;
  const showPreviewButton =
    campaign?.status === 'published' &&
    campaign?.snippetStatus?.status === 'active' &&
    variant.status === 'published' &&
    !!landingPageUrl &&
    !!variant.id;
  const previewRoute = showPreviewButton
    ? `${workspaceBaseUrl}/campaigns/${campaignId}/landing-pages/${variantId}/preview`
    : null;

  const tabItems = [
    {
      title: 'Analytics',
      url: `${workspaceBaseUrl}/campaigns/${campaignId}/landing-pages/${variantId}`,
    },
    {
      title: 'Tracked events',
      url: `${workspaceBaseUrl}/campaigns/${campaignId}/landing-pages/${variantId}/tracked-events`,
    },
  ];

  const canCopyUrl = variant.status === 'published' && !!variant.staticUrl;

  const handleCopyVariantUrl = () => {
    if (!variant.staticUrl) return;
    navigator.clipboard.writeText(variant.staticUrl);
    toast.success('Copied link to clipboard');
  };

  const prevTo = prevVariantId
    ? `${workspaceBaseUrl}/campaigns/${campaignId}/landing-pages/${prevVariantId}`
    : null;
  const nextTo = nextVariantId
    ? `${workspaceBaseUrl}/campaigns/${campaignId}/landing-pages/${nextVariantId}`
    : null;

  return (
    <div className="flex flex-1 flex-col">
      <header className="sticky top-0 z-30">
        <LayoutTopbar breadcrumbs={breadcrumbs} />
      </header>

      {/* Top section */}
      <div className="px-8 pt-8">
        <div className="flex flex-col gap-12 max-w-6xl mx-auto">
          <div className="flex flex-col gap-4">
          {/* Prev/next variant switcher (top-right) */}
          <div className="flex justify-end">
            <VariantSwitcher prevTo={prevTo} nextTo={nextTo} />
          </div>

          {/* Header: company info (left) + URL actions (right) */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-2">
              <Avatar className="size-8">
                {companyLogoUrl && (
                  <AvatarImage src={companyLogoUrl} alt={companyName} />
                )}
                <AvatarFallback className="text-xs">
                  {companyInitials}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col gap-0">
                <h1 className="font-heading text-3xl font-semibold leading-9 tracking-[-0.5px] text-foreground">
                  {companyName}
                </h1>
                <p className="font-heading text-3xl font-semibold leading-9 tracking-[-0.5px] text-neutral-400">
                  Results of your campaign for this account
                </p>
                <div className="flex flex-wrap items-center gap-1 pt-1">
                  <PageStatusBadge status={variant.status ?? null} />
                  <PageOutdatedStatusBadge status={variant.outdatedStatus} />
                  {campaign?.name && (
                    <span className="flex items-center justify-center gap-1 rounded-md border border-border bg-background px-2 py-0.5 text-xs font-semibold text-foreground">
                      {campaign.name}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {previewRoute && (
                <Button
                  variant="outline"
                  size="icon"
                  className="size-9"
                  asChild
                  aria-label="Open internal preview in new tab"
                >
                  <a href={previewRoute} target="_blank" rel="noreferrer">
                    <ExternalLink className="size-4" />
                  </a>
                </Button>
              )}
              <Button
                className="h-9"
                disabled={!canCopyUrl}
                onClick={handleCopyVariantUrl}
              >
                <Link2 className="size-4" />
                Copy URL
              </Button>
              <PageActionsMenu
                page={variant}
                campaign={campaign}
                triggerVariant="outline"
                triggerClassName="size-9"
              />
            </div>
          </div>
          </div>

          {/* Tabs */}
          <TabNavLinks items={tabItems} className="px-0" />
        </div>
      </div>

      {/* Body: content at top, footer pinned at bottom */}
      <div className="flex flex-1 flex-col px-8 py-12">
        <div className="flex flex-1 flex-col max-w-6xl mx-auto w-full">
          <div className="flex-1">
            <Outlet />
          </div>

          {/* Footer dates */}
          <div className="flex items-center justify-between text-xs pt-8">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Generated</span>
              <span className="font-medium text-card-foreground">
                {formatDate(variant.createdAt)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Last updated</span>
              <span className="font-medium text-card-foreground">
                {formatDate(variant.updatedAt)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Published</span>
              <span className="font-medium text-card-foreground">
                {formatDate(variant.lastPublishedAt)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

type VariantSwitcherProps = {
  prevTo: string | null;
  nextTo: string | null;
};

function VariantSwitcher({ prevTo, nextTo }: VariantSwitcherProps) {
  const baseClass =
    'flex size-9 items-center justify-center border border-input bg-background shadow-xs transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50';

  return (
    <div className="flex items-start">
      {prevTo ? (
        <Link
          to={prevTo}
          className={`${baseClass} rounded-l-md -mr-px`}
          aria-label="Previous variant"
        >
          <ChevronLeft className="size-4" />
        </Link>
      ) : (
        <button
          type="button"
          disabled
          className={`${baseClass} rounded-l-md -mr-px`}
          aria-label="Previous variant"
        >
          <ChevronLeft className="size-4" />
        </button>
      )}
      {nextTo ? (
        <Link
          to={nextTo}
          className={`${baseClass} rounded-r-md`}
          aria-label="Next variant"
        >
          <ChevronRight className="size-4" />
        </Link>
      ) : (
        <button
          type="button"
          disabled
          className={`${baseClass} rounded-r-md`}
          aria-label="Next variant"
        >
          <ChevronRight className="size-4" />
        </button>
      )}
    </div>
  );
}
